import {useCallback, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {authorizationHeaderFromAccessToken} from '../auth'
import {API_URL} from '../config'
import Learner, {
  LearnerId,
  IndividualLearner as LearnerT
} from '../domain/learner'
import {captureException} from '../errors'
import ServerError from '../errors/server'
import {getAccessToken} from '../store/authentication/authentication.selectors'

type Options = Readonly<{
  learnerId: LearnerId
  onError?: (error: Error) => void
  onSuccess?: (learner: Readonly<LearnerT>) => void
}>

function useLearner({learnerId, onError, onSuccess}: Options): {
  error: Error | null
  isLoading: boolean
  learner: LearnerT | undefined
  refetchLearner: () => void
} {
  const accessToken = useSelector(getAccessToken)
  const [learner, setLearner] = useState<Readonly<LearnerT> | undefined>(
    undefined
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchLearner = useCallback(() => {
    setError(null)
    setIsLoading(true)
    getLearner(accessToken, learnerId)
      .then(learner => {
        setLearner(learner)
        onSuccess?.(learner)
      })
      .catch(error => {
        captureException(error)
        setError(error)
        onError?.(error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [accessToken, onError, onSuccess, learnerId])

  useEffect(() => {
    fetchLearner()
  }, [fetchLearner])

  return {error, isLoading, learner, refetchLearner: fetchLearner}
}

async function getLearner(
  accessToken: string,
  learnerId: LearnerId
): Promise<Readonly<LearnerT>> {
  const url = new URL(`learners/${learnerId}`, API_URL).toString()
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...authorizationHeaderFromAccessToken(accessToken)
    }
  })
  if (response.status !== 200) {
    return ServerError.fromResponse(response)
  }

  const body = await response.json()

  return parseResponseBody(body)
}

export function parseResponseBody(candidate: unknown): Readonly<LearnerT> {
  if (typeof candidate !== 'object' || candidate === null) {
    throw new Error('Candidate is not an object')
  }
  try {
    return Learner.parseIndividualLearner(candidate)
  } catch (error) {
    throw new Error(
      `Unexpected response body. Expected an IndividualLearner. ${error.message}`,
      {cause: error}
    )
  }
}

export default useLearner
